// Chemical usage → next year's product order. Usage is what FieldRoutes recorded over the
// last 12 months; next-year need = active plans × visits per year × average per service.
// Growth %, on hand and container size are inputs on this page; the order quantity is
// computed from them (nothing is saved).
import React, { useEffect, useMemo, useState } from "react";
import api from "../../../apiClient";
import { errorText, qty } from "./useChemicalsApi";
import "./Chemicals.css";

const OFFICE = { 1: "Florida", 3: "Michigan" };

function ServiceTypes({ rows }) {
  return (
    <section className="chem-card">
      <h2>Every service type</h2>
      <div className="chem-sub" style={{ marginBottom: "var(--s-3)" }}>Nothing is filtered out. Pick which ones to order for.</div>
      <div className="chem-table-wrap">
        <table className="chem-table">
          <thead><tr><th>Service type</th><th>Office</th><th className="num">Services, 12 months</th><th className="num">With a chemical logged</th>
            <th className="num">Active plans</th><th className="num">Expected services, next year</th></tr></thead>
          <tbody>{rows.map((s) => (
            <tr key={`${s.office_id}-${s.service_type_id}`}>
              <td>{s.service_type}</td><td>{OFFICE[s.office_id] || `Office ${s.office_id}`}</td>
              <td className="num">{s.services_performed.toLocaleString()}</td>
              <td className="num">{s.coverage_pct != null ? `${s.coverage_pct}%` : "—"}</td>
              <td className="num">{s.active_plans || "—"}</td>
              <td className="num">{s.expected_services_next_year ? Math.round(s.expected_services_next_year).toLocaleString() : "—"}
                {s.plans_without_frequency ? <div className="chem-detail">{s.plans_without_frequency} plans have no frequency</div> : null}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </section>
  );
}

function NextYear({ rows }) {
  const [growth, setGrowth] = useState("0");
  const [inputs, setInputs] = useState({});
  const g = Number(growth) || 0;
  const set = (key, field) => (e) => setInputs((s) => ({ ...s, [key]: { ...(s[key] || {}), [field]: e.target.value } }));
  return (
    <section className="chem-card">
      <div className="chem-head">
        <h2>Next year's need</h2>
        <div className="chem-form"><label>Growth %<input type="number" step="1" value={growth} onChange={(e) => setGrowth(e.target.value)} style={{ width: 90 }} /></label></div>
      </div>
      <div className="chem-sub" style={{ marginBottom: "var(--s-3)" }}>Need = active plans × visits per year × average used per service, plus growth. Order = need − on hand, rounded up to whole containers.</div>
      <div className="chem-table-wrap">
        <table className="chem-table">
          <thead><tr><th>Product</th><th>Office</th><th className="num">Used, last 12 months</th><th className="num">Need, next year</th>
            <th className="num">On hand</th><th className="num">Container size</th><th className="num">Order</th></tr></thead>
          <tbody>{rows.map((r) => {
            const key = `${r.office_id}-${r.product_id || r.product}`;
            const inp = inputs[key] || {};
            const need = r.need * (1 + g / 100);
            const onHand = Number(inp.on_hand) || 0;
            const size = Number(inp.container) || 0;
            const short = Math.max(0, need - onHand);
            const order = size > 0 ? `${Math.ceil(short / size)} × ${qty(size, r.unit)}` : qty(short, r.unit);
            return (
              <tr key={key}>
                <td>{r.product}<div className="chem-detail">{r.epa_reg_number ? `EPA ${r.epa_reg_number}` : ""}</div></td>
                <td>{OFFICE[r.office_id] || `Office ${r.office_id}`}</td>
                <td className="num">{qty(r.last_year_total, r.unit)}</td>
                <td className="num">{qty(need, r.unit)}</td>
                <td className="num"><input className="chem-cell" type="number" step="any" min="0" value={inp.on_hand || ""} onChange={set(key, "on_hand")} placeholder={r.unit} /></td>
                <td className="num"><input className="chem-cell" type="number" step="any" min="0" value={inp.container || ""} onChange={set(key, "container")} placeholder={r.unit} /></td>
                <td className="num">{order}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    </section>
  );
}

function Usage({ rows }) {
  return (
    <section className="chem-card">
      <h2>Usage by service type</h2>
      <div className="chem-table-wrap">
        <table className="chem-table">
          <thead><tr><th>Service type</th><th>Product</th><th className="num">Total</th><th className="num">Applications</th>
            <th className="num">Services performed</th><th className="num">Average per service</th><th>Logged as</th></tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i}>
              <td>{r.service_type}<div className="chem-detail">{OFFICE[r.office_id] || `Office ${r.office_id}`}</div></td>
              <td>{r.product}</td>
              <td className="num">{qty(r.total, r.unit)}{r.applications_with_amount < r.applications ? <div className="chem-detail">{r.applications - r.applications_with_amount} with no amount</div> : null}</td>
              <td className="num">{r.applications}</td>
              <td className="num">{r.services_performed}</td>
              <td className="num">{r.avg_per_service != null ? qty(r.avg_per_service, r.unit) : "—"}</td>
              <td className="chem-detail">{r.logged_units.join(", ")}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </section>
  );
}

function Issues({ issues, totals }) {
  return (
    <section className="chem-card">
      <h2>Issues</h2>
      <div className="chem-stats" style={{ marginBottom: "var(--s-3)" }}>
        <div className="chem-stat"><div className="chem-stat__val">{totals.coverage_pct != null ? `${totals.coverage_pct}%` : "—"}</div><div className="chem-stat__label">Completed services with a chemical logged</div></div>
        <div className="chem-stat"><div className="chem-stat__val">{issues.no_quantity_logged.toLocaleString()}</div><div className="chem-stat__label">Applications with no amount</div></div>
        <div className="chem-stat"><div className="chem-stat__val">{issues.odd_or_missing_unit.count}</div><div className="chem-stat__label">Missing or unusual unit</div></div>
        <div className="chem-stat"><div className="chem-stat__val">{issues.not_in_catalog.length}</div><div className="chem-stat__label">Products not in the catalog</div></div>
      </div>
      <h2>Coverage by technician</h2>
      <div className="chem-table-wrap">
        <table className="chem-table">
          <thead><tr><th>Technician</th><th className="num">Completed services</th><th className="num">With a chemical logged</th></tr></thead>
          <tbody>{issues.coverage_by_tech.map((t) => (
            <tr key={t.tech}><td>{t.tech}</td><td className="num">{t.services}</td><td className="num">{t.coverage_pct != null ? `${t.coverage_pct}%` : "—"}</td></tr>
          ))}</tbody>
        </table>
      </div>
      <h2 style={{ marginTop: "var(--s-4)" }}>Re-service within 30 days of an application</h2>
      <div className="chem-sub" style={{ marginBottom: "var(--s-2)" }}>A signal to look at, not a conclusion: a callback can have many causes.</div>
      <div className="chem-table-wrap">
        <table className="chem-table">
          <thead><tr><th>Product</th><th className="num">Applications</th><th className="num">Followed by a re-service</th></tr></thead>
          <tbody>{issues.reservice_within_30_days.slice(0, 40).map((r) => (
            <tr key={r.product}><td>{r.product}</td><td className="num">{r.applications}</td><td className="num">{r.rate_pct != null ? `${r.rate_pct}%` : "—"}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </section>
  );
}

export default function ChemicalUsagePage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("need");
  useEffect(() => {
    api.get("/inventory/reports/chemical-usage/").then((r) => setData(r.data))
      .catch((e) => setError(errorText(e, "Couldn't load the usage report.")));
  }, []);
  const period = useMemo(() => (data ? `${data.window.since} to ${data.window.until}` : ""), [data]);
  if (error) return <div className="chem-page"><div className="chem-msg chem-msg--err">{error}</div></div>;
  if (!data) return <div className="chem-page"><div className="chem-empty">Building the report…</div></div>;
  return (
    <div className="chem-page">
      <div className="chem-head">
        <div>
          <h1>Chemical usage and next year's order</h1>
          <div className="chem-sub">From FieldRoutes, {period}. {data.totals.applications.toLocaleString()} applications across {data.totals.services_performed.toLocaleString()} completed services.</div>
        </div>
        <div className="chem-seg">
          {[["need", "Next year"], ["usage", "Usage"], ["types", "Service types"], ["issues", "Issues"]].map(([k, l]) => (
            <button key={k} type="button" className={tab === k ? "is-active" : ""} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
      </div>
      {tab === "need" ? <NextYear rows={data.next_year} /> : null}
      {tab === "usage" ? <Usage rows={data.usage} /> : null}
      {tab === "types" ? <ServiceTypes rows={data.service_types} /> : null}
      {tab === "issues" ? <Issues issues={data.issues} totals={data.totals} /> : null}
    </div>
  );
}
